import { NOTIFICATION_TYPES } from "../../../../../config/constants";
import type { ColumnsType } from "antd/es/table";
import {
  Empty,
  Select,
  Button,
  Form,
  Input,
  Checkbox,
  Table,
  Tag,
  FormInstance,
  InputRef,
  TablePaginationConfig, TableProps, Tooltip,
} from "antd";
import "./Mapping.css";
import {CheckOutlined, CloseOutlined, FilterFilled, FilterOutlined , CaretUpOutlined, CaretDownOutlined} from "@ant-design/icons";
import { useContext, useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import styles from "./Mapping.module.css";
import React from "react";
import FieldMappingsService from "../../../../../services/fieldmappings/FieldMappingsService";
import { GetFieldMappingsRequest } from "../../../../../models/fieldmappings/GetFieldMappingsRequest";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";
import { useDispatch, useSelector } from "react-redux";
import { setRecordsPerPage } from "../../../../../redux/actions";
import ApplicationService from "../../../../../services/application/ApplicationService";
import { TABLE_SETUP_HIDE_SYSTEM_FIELDS } from "../../../../../config/consts/appsettings";
import Search from "antd/lib/input/Search";

interface UnmappedDataType {
  key: string;
  name: string;
}

interface DraggableBodyRowProps
    extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
}

interface DroppableBodyRowProps
    extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  mapRow: (dragIndex: number, hoverIndex: number) => void;
}

interface TableDataType {
  key: string;
  id: number;
  fromValue: string;
  toField: string;
  mandatory: boolean;
  comments: string;
  fromTableFields: any;
  fromTableId: string | null;
  fromFieldId: string | null;
  fromField: string | null;
  toTableId: number;
  toFieldId: number;
}

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
  key: string;
  fromValue: string;
  toField: string;
  comments: string;
  fromFieldId: string | null;
  fromField: string | null;
}

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

type EditableTableProps = Parameters<typeof Table>[0];

type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

type OnChange = NonNullable<TableProps<UnmappedDataType>['onChange']>;
type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;

const type = "DraggableBodyRow";

const Mapping = ({
                   destinationTables,
                   selectedDestinationTable,
                   setSelectedDestinationTable,
                   setFieldMappings,
                   setIsTableLoading,
                   unmappedFields,
                   setUnmappedFields,
                   sourceTableFields,
                   fieldMappings,
                   editableFieldMappings,
                   setEditableFieldMappings,
                   isTableLoading,
                   selectedEntity,
                   selectedSourceTable,
                   editEnabled,
                   setEditEnabled,
                   triggerUpdate,
                   setTriggerUpdate
                 }: {
  destinationTables: Array<any>;
  selectedDestinationTable: any;
  setSelectedDestinationTable: any;
  setFieldMappings: any;
  setIsTableLoading: any;
  unmappedFields: any;
  setUnmappedFields: any;
  sourceTableFields: any;
  fieldMappings: any;
  editableFieldMappings: TableDataType[];
  setEditableFieldMappings: any;
  isTableLoading: any;
  selectedEntity: any;
  selectedGroupCode: any;
  selectedSourceTable: any;
  editEnabled: any;
  setEditEnabled: any;
  triggerUpdate: any;
  setTriggerUpdate: any;
}) => {
  const { t, i18n } = useTranslation("translations");

  let locale = {
    emptyText: [
      <Empty
          description={t("General.Labels.NoData", {
            lngs: i18n.languages,
            defaultValue: "No data to display",
          })}
          image={Empty.PRESENTED_IMAGE_DEFAULT}
      />,
    ],
  };

  const { recordsPerPage } = useSelector((state: any) => ({
    recordsPerPage: state.recordsPerPage,
  }));

  const dispatch = useDispatch();
  const [fromFieldFilterVisible, setFromFieldFilterVisible] = useState<boolean>(false);
  const [toFieldFilterVisible, setToFieldFilterVisible] = useState<boolean>(false);
  const [commentsFilterVisible, setCommentsFilterVisible] = useState<boolean>(false);


  const [applyFromFieldSearchInput, setApplyFromFieldSearchInput] = useState<string>("");
  const [applyToFieldSearchInput, setApplyToFieldSearchInput] = useState<string>("");
  const [applyCommentSearchInput, setApplyCommentSearchInput] = useState<string>("");


  const [showUnmappedOnly, setShowUnmappedOnly] = useState<boolean>(false);
  const [showMandatoryOnly, setShowMandatoryOnly] = useState<boolean>(false);

  const [applyFieldsSearchInput, setApplyFieldsSearchInput] = useState<string>("");
  const [filteredFieldsData, setFilteredFieldsData] = useState<any>();
  const [sortedInfo, setSortedInfo] = useState<Sorts>({});
  const [mappingSortedInfo, setMappingSortedInfo] = useState<Sorts>({});

  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(
      recordsPerPage ?? 20
  );

  const [filteredEditableFieldMappings, setFilteredEditableFieldMappings] = useState<TableDataType[]>([]);

  const [showSystemFields, setShowSystemFields] = useState<boolean>(false);

  useEffect(() => {
    ApplicationService.getPreferences().then((response: any) => {
      var hideSystemFieldsPreference = response.data.find(
          (p) =>
              p.key === TABLE_SETUP_HIDE_SYSTEM_FIELDS &&
              p.object === "Application"
      );
      if (hideSystemFieldsPreference) {
        setShowSystemFields(hideSystemFieldsPreference.value !== "true");
      }
    });
  }, []);

  useEffect(() => {
    setFilteredEditableFieldMappings(editableFieldMappings);
  }, [editableFieldMappings]);

  useEffect(() => {
    let filteredMappings = editableFieldMappings;

    if (applyFromFieldSearchInput || applyToFieldSearchInput || applyCommentSearchInput) {
      filteredMappings = filteredMappings.filter((mapping) => {
        const fromFieldMatch = mapping.fromField?.toLowerCase().includes(applyFromFieldSearchInput.toLowerCase()) || mapping.fromValue?.toLowerCase().includes(applyFromFieldSearchInput.toLowerCase());
        const toFieldMatch = mapping.toField?.toLowerCase().includes(applyToFieldSearchInput.toLowerCase());
        const commentsMatch = mapping.comments?.toLowerCase().includes(applyCommentSearchInput.toLowerCase());
        return fromFieldMatch && toFieldMatch && commentsMatch;
      });
    }

    if (showUnmappedOnly) {
      filteredMappings = filteredMappings.filter(
          (el: any) => el.fromFieldId == null && !el.fromValue
      );
    }

    if (showMandatoryOnly) {
      filteredMappings = filteredMappings.filter(
          (el: any) => el.mandatory
      );
    }

    if (!showSystemFields) {
      filteredMappings = filteredMappings.filter(
          (x: any) => !x.isSystemField
      );
    }

    // Reapply sorting only if a sorting order is specified
    if (mappingSortedInfo.columnKey && mappingSortedInfo.order) {
      const sortedData = [...filteredMappings].sort((a, b) => {
        let aColumn = a[mappingSortedInfo.columnKey];
        let bColumn = b[mappingSortedInfo.columnKey];

        if (mappingSortedInfo.columnKey == 'fromValue') {
          aColumn = a.fromValue && a.fromValue !== "" ? a.fromValue : (a.fromField ?? "NOT MAPPED");
          bColumn = b.fromValue && b.fromValue !== "" ? b.fromValue : (b.fromField ?? "NOT MAPPED");
        }
        return mappingSortedInfo.order === 'ascend'
            ? aColumn.trim().localeCompare(bColumn.trim())
            : bColumn.trim().localeCompare(aColumn.trim());
      });
      setFilteredEditableFieldMappings(sortedData);
      return;
    }

    setFilteredEditableFieldMappings(filteredMappings);
  }, [editableFieldMappings, applyFromFieldSearchInput, applyToFieldSearchInput, applyCommentSearchInput, showUnmappedOnly, showMandatoryOnly, showSystemFields]);
  const destinationTableRef = useRef<any>();

  useEffect(() => {
    setFilteredFieldsData(unmappedFields);
  }, [unmappedFields])

  const removeFromField = (element: any) => {
    const newData = [...editableFieldMappings];
    const index = newData.findIndex(
        (item) =>
            element.toFieldId === item.toFieldId && element.toTableId === item.toTableId
    );

    newData[index].fromTableFields.splice(0, 1);
    newData[index].fromFieldId = null;
    newData[index].fromField = "";

    setEditableFieldMappings(newData);

    const newUnmappedFields = [
      ...unmappedFields,
      {
        id: element.fromFieldId,
        tableId: element.fromTableId,
        name: element.fromField,
      },
    ];
    setUnmappedFields(newUnmappedFields);
    // element.fromTableFields.splice(0, 1);
    // element.fromFieldId = null;
    // element.fromField = "";
  };

  const columns: ColumnsType<UnmappedDataType> = [
    {
      title: t(
          "Page.Operation.LoadfileSetup.SideTable.Headers.UnmappedFields",
          {
            lngs: i18n.languages,
            defaultValue: "UnmappedfFields",
          }
      ),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
    },
  ];

  const handleFieldsDataTableChange: OnChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const handleSort = (order: 'ascend' | 'descend' | null, key: string) => {
    let newOrder = order;
    if (mappingSortedInfo.order === order) {
      newOrder = null; // Revert to initial state
    }
    setMappingSortedInfo({ order: newOrder, columnKey: key });

    if (newOrder) {
      const sortedData = [...filteredEditableFieldMappings].sort((a, b) => {
        let aKey = a[key];
        let bKey = b[key];
        
        if (key == 'fromValue') {
          aKey = a[key] && a[key] !== "" ? a[key] : (a.fromField ?? "NOT MAPPED");
          bKey = b[key] && b[key] !== "" ? b[key] : (b.fromField ?? "NOT MAPPED");  
        }
        
        return newOrder === 'ascend'
            ? aKey.trim().localeCompare(bKey.trim())
            : bKey.trim().localeCompare(aKey.trim());
      });
      setFilteredEditableFieldMappings(sortedData);
    } else {
      setFilteredEditableFieldMappings([...editableFieldMappings]); // Revert to initial state
    }
  };

  const columnsTable: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: () => (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontSize: 14, fontWeight: "bold", display: "flex", alignItems: "center" }}>
                {t(
                    "Page.Operation.LoadFileSetup.Table.Headers.FromField",
                    {
                      lngs: i18n.languages,
                      defaultValue: "From field",
                    }
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <span className="ant-table-column-sorter">
                  <span className="ant-table-column-sorter-inner"
                        style={{cursor: 'pointer'}}      
                        onClick={() => handleSort(mappingSortedInfo.columnKey == 'fromValue' ? (mappingSortedInfo.order == 'ascend' ? 'descend' : mappingSortedInfo.order == 'descend' ? null : 'ascend') : 'ascend', 'fromValue')}>
                    <span
                        style={{ width: '20px', right: '0' }}
                        role="button"
                        className={`ant-table-column-sorter-up ${mappingSortedInfo.columnKey == 'fromValue' && mappingSortedInfo.order === 'ascend' ? 'active' : ''}`}
                        
                    >
                      <CaretUpOutlined />
                    </span>
                    <span
                        style={{ width: '20px', right: '0' }}
                        role="button"
                        className={`ant-table-column-sorter-down ${mappingSortedInfo.columnKey == 'fromValue' && mappingSortedInfo.order === 'descend' ? 'active' : ''}`}
                    >
                      <CaretDownOutlined />
                    </span>
                  </span>
                </span>
                {!fromFieldFilterVisible ? (
                    <FilterOutlined style={{ paddingTop: 4 }} onClick={() => setFromFieldFilterVisible(true)} />
                ) : (
                    <FilterFilled style={{ paddingTop: 4 }} onClick={() => setFromFieldFilterVisible(false)} />
                )}
              </div>
            </div>
            {fromFieldFilterVisible && (
                <Search
                    placeholder="Search"
                    value={applyFromFieldSearchInput}
                    onChange={(e) => setApplyFromFieldSearchInput(e.target.value)}
                />
            )}
          </>
      ),
      dataIndex: "fromValue",
      key: "fromValue",
      editable: true,
      // sorter: handleFromValueSort,
      render: (text, row: any) => {
        if (!row.fromField && !row.fromValue) {
          return <Tag color="red">NOT MAPPED</Tag>;
        } else {
          if (row.fromField) {
            return (
                <div className={styles.tableRow}>
                  {row.fromField}
                  {editEnabled && (
                      <Button
                          icon={<CloseOutlined />}
                          type="ghost"
                          size="small"
                          className={styles.rowRemoveMappingIcon}
                          onClick={() => removeFromField(row)}
                      />
                  )}
                </div>
            );
          }
          if (row.fromValue && row.fromValue !== "") {
            return <div>{row.fromValue}</div>;
          }
          return text;
        }
      },
      shouldCellUpdate: (record, prevRecord) => true,
    },
    {
      title: () => (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, fontWeight: "bold", display: "flex", alignItems: "center" }}>
                {t(
                    "Page.Operation.LoadFileSetup.Table.Headers.ToField",
                    {
                      lngs: i18n.languages,
                      defaultValue: "To field",
                    }
                )}
              </span>
              <div style={{display: "flex", alignItems: "center"}}>
                <span className="ant-table-column-sorter">
                  <span className="ant-table-column-sorter-inner"
                        style={{cursor: 'pointer'}}
                        onClick={() => handleSort(mappingSortedInfo.columnKey == 'toField' ? (mappingSortedInfo.order == 'ascend' ? 'descend' : mappingSortedInfo.order == 'descend' ? null : 'ascend') : 'ascend', 'toField')}>
                    <span
                        style={{width: '20px', right: '0'}}
                        role="button"
                        className={`ant-table-column-sorter-up ${mappingSortedInfo.columnKey == 'toField' && mappingSortedInfo.order === 'ascend' ? 'active' : ''}`}
                    >
                      <CaretUpOutlined/>
                    </span>
                    <span
                        style={{width: '20px', right: '0'}}
                        role="button"
                        className={`ant-table-column-sorter-down ${mappingSortedInfo.columnKey == 'toField' && mappingSortedInfo.order === 'descend' ? 'active' : ''}`}
                    >
                      <CaretDownOutlined/>
                    </span>
                  </span>
                </span>
                {!toFieldFilterVisible ? (
                    <FilterOutlined style={{paddingTop: 4}} onClick={() => setToFieldFilterVisible(true)}/>
                ) : (
                    <FilterFilled style={{paddingTop: 4}} onClick={() => setToFieldFilterVisible(false)}/>
                )}
              </div>
            </div>
            {toFieldFilterVisible && (
                <Search
                    placeholder="Search"
                    value={applyToFieldSearchInput}
                    onChange={(e) => setApplyToFieldSearchInput(e.target.value)}
                />
            )}
          </>
      ),
      dataIndex: "toField",
      key: "toField",
      render: (text, row: any) => {
        if (row.mandatory) {
          return <Tooltip
              placement="right"
              title={row.description}
          >
            <span className={styles.tableRowFieldRequired}>{row.label ? row.toField + " | " + row.label : row.toField}</span>
          </Tooltip>
          
        } else {
          return <Tooltip 
              placement="right"
              title={row.description}
          >
            <span>{row.label ? row.toField + " | " + row.label : row.toField}</span>
          </Tooltip>
        }
      },
    },
    {
      title: () => (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, fontWeight: "bold", display: "flex", alignItems: "center" }}>Comments</span>
              <div style={{display: "flex", alignItems: "center"}}>
                <span className="ant-table-column-sorter"></span>
                {!commentsFilterVisible ? (
                    <FilterOutlined style={{paddingTop: 4}} onClick={() => setCommentsFilterVisible(true)}/>
                ) : (
                    <FilterFilled style={{paddingTop: 4}} onClick={() => setCommentsFilterVisible(false)}/>
                )}
              </div>
            </div>
            {commentsFilterVisible && (
                <Search
                    placeholder="Search"
                    value={applyCommentSearchInput}
                    onChange={(e) => setApplyCommentSearchInput(e.target.value)}
                />
            )}
          </>
      ),
      dataIndex: "comments",
      key: "comments",
      editable: true,
      render: (text, row: any) => {
        if (text.length === 0) {
          return <div style={{ opacity: 0 }}>/</div>;
        }
        return text;
      },
      shouldCellUpdate: (record, prevRecord) => true,
    },
  ];

  const EditableCell: React.FC<EditableCellProps> = ({
                                                       title,
                                                       editable,
                                                       children,
                                                       dataIndex,
                                                       record,
                                                       handleSave,
                                                       ...restProps
                                                     }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<InputRef>(null);
    const inputCursorPosition = 0;
    const form = useContext(EditableContext)!;

    useEffect(() => {
      if (editing) {
        inputRef.current!.focus();
      }
    }, [editing]);

    const toggleEdit = () => {
      setEditing(!editing);
      form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    };

    const save = async () => {
      try {
        const values = await form.validateFields();
        toggleEdit();
        record.fromValue = values.fromValue;
        handleSave({ ...record, ...values });
      } catch (errInfo) {
        console.log("Save failed:", errInfo);
      }
    };

    let childNode = children;

    if (editable && editEnabled && !record.fromFieldId && !record.fromField) {
      childNode = editing ? (
          <Form.Item name={dataIndex}>
            <Input
                ref={inputRef}
                onBlur={() => {
                  save();
                  inputRef.current?.setSelectionRange(
                      inputCursorPosition,
                      inputCursorPosition
                  );
                }}
            />
          </Form.Item>
      ) : (
          <div className="editable-cell-value-wrap" onClick={toggleEdit}>
            {children}
          </div>
      );
    }

    return <td {...restProps}>{childNode}</td>;
  };

  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };
  const tableItemsComponents = {
    body: {
      row: DroppableBodyRow,
      cell: EditableCell,
    },
  };

  const handleSave = (row: TableDataType) => {
    const newData = [...editableFieldMappings];
    const index = newData.findIndex(
        (item) =>
            row.toFieldId === item.toFieldId && row.toTableId === item.toTableId
    );
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setEditableFieldMappings(newData);
  };

  const editableColumns = columnsTable.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: TableDataType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const handleDestinationChange = (value: number) => {
    setIsTableLoading(true);
    setSelectedDestinationTable(value);
  };

  const mapRow = (dragIndex: number, hoverIndex: number) => {
    const filteredIndex = hoverIndex + currentPageSize * (currentPageNumber - 1);
    const fieldMapping = filteredEditableFieldMappings[filteredIndex];
    const idx = editableFieldMappings.findIndex(m => m.toFieldId === fieldMapping.toFieldId);

    let fieldsData = [...filteredFieldsData];
    if (sortedInfo.order === "ascend") {
      fieldsData.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortedInfo.order === "descend") {
      fieldsData.sort((a, b) => b.name.localeCompare(a.name));
    }

    const dragRow = fieldsData[dragIndex];

    let newTableItems = editableFieldMappings.map(x => ({ ...x }));
    newTableItems[idx].fromFieldId = dragRow.id;
    newTableItems[idx].fromField = dragRow.name;
    newTableItems[idx].fromTableFields = [
      {
        fromFieldId: dragRow.id,
        fromField: dragRow.name,
        tableId: dragRow.tableId,
      },
    ];
    setEditableFieldMappings([...newTableItems]);

    // Remove the mapped field from the unmapped fields list
    const newUnmappedFields = unmappedFields.filter(x => x.id !== dragRow.id);
    setUnmappedFields(newUnmappedFields);
  };

  const getMappingsDataSource = () => {
    let res =  filteredEditableFieldMappings.map((x: any) => { return { ...x } });

    res = res.filter((mapping) => {
      const fromFieldMatch = mapping.fromField?.toLowerCase().includes(applyFromFieldSearchInput.toLowerCase()) || mapping.fromValue?.toLowerCase().includes(applyFromFieldSearchInput.toLowerCase());
      const toFieldMatch = mapping.toField?.toLowerCase().includes(applyToFieldSearchInput.toLowerCase());
      const commentsMatch = mapping.comments?.toLowerCase().includes(applyCommentSearchInput.toLowerCase());
      return fromFieldMatch && toFieldMatch && commentsMatch;
    });

    if (showUnmappedOnly) {
      res = res.filter(
          (el: any) => el.fromFieldId == null && !el.fromValue
      );
    }

    if (showMandatoryOnly) {
      res = res.filter(
          (el: any) => el.mandatory
      );
    }

    if (!showSystemFields) {
      res = res.filter(
          (x: any) => !x.isSystemField
      );
    }

    return res;
  };

  const onCancelEdit = () => {
    setTriggerUpdate(!triggerUpdate)
    setEditableFieldMappings(fieldMappings);
  };

  const onSaveClick = () => {
    const models = editableFieldMappings.map((element) => ({
      id: element.id,
      fromTableId:
          element.fromTableId != null
              ? element.fromTableId
              : parseInt(selectedSourceTable, 10),
      fromFieldId: element.fromFieldId,
      fromValue:
          element.fromFieldId != null ? element.fromField : element.fromValue,
      toTableId: element.toTableId,
      toFieldId: element.toFieldId,
      comments: element.comments,
    }));

    FieldMappingsService.bulkUpdate(models)
        .then((response: any) => {
          setIsTableLoading(true);
          NotificationsUtil.openNotificationDialog({
            description: t(
                "Page.Operation.LoadfileSetup.Notifications.Mapping.UpdateSuccessDescription",
                {
                  lngs: i18n.languages,
                  defaultValue: "Mappings successfully saved.",
                }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
          const model: GetFieldMappingsRequest = {
            groupCode: "",
            fromTableId: selectedSourceTable,
            toTableId: selectedDestinationTable,
          };
          FieldMappingsService.getAll(model).then((response: any) => {
            const data = response.data;
            setFieldMappings(
                data.map((x: any) => ({
                  ...x,
                  fromValue: x.fromValue === null ? "" : x.fromValue,
                  comments: x.comments === null ? "" : x.comments,
                  fromField: x.fromField === null ? "" : x.fromField,
                  fromTableFields:
                      x.fromFieldId != null
                          ? [
                            {
                              fromFieldId: x.fromFieldId,
                              fromField: x.fromField,
                              tableId: x.fromTableId,
                            },
                          ]
                          : [],
                }))
            );
            setIsTableLoading(false);
            setEditEnabled(false);
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
  };

  const onPageChange = (page: any, pageSize: any) => {
    setCurrentPageNumber(page);
    setCurrentPageSize(pageSize);
    dispatch(setRecordsPerPage(pageSize));
  };

  const pagination: TablePaginationConfig = {
    defaultPageSize: currentPageSize,
    current: currentPageNumber,
    pageSize: currentPageSize,
    onChange: onPageChange,
    showQuickJumper: true,
    showSizeChanger: true,
    locale: {
      items_per_page: t("General.Table.Pagination.ItemPerPage", {
        lngs: i18n.languages,
        defaultValue: "/page",
      })!,
      page: t("General.Table.Pagination.Page", {
        lngs: i18n.languages,
        defaultValue: "page",
      })!,
      jump_to: t("General.Table.Pagination.GoTo", {
        lngs: i18n.languages,
        defaultValue: "Go to",
      })!,
    },
    showTotal: (total: number, range: [number, number]) => {
      const translation = t("General.Table.Pagination.RangeRecords", {
        lngs: i18n.languages,
        defaultValue: `${range[0]}-${range[1]} out of ${total} records`,
      });
      return `${translation
          .replace("${range[0]}", range[0].toString())
          .replace("${range[1]}", range[1].toString())
          .replace("${total}", total.toString())}`;
    },
    itemRender: (page: number, type, originalElement) => {
      return originalElement;
    },
  };

  const isFilterEnabled = () => {
    return fromFieldFilterVisible || toFieldFilterVisible || commentsFilterVisible;
  }

  const scroll = { y: isFilterEnabled() ? "calc(100vh - 390px)" : "calc(100vh - 360px)" };

  useEffect(() => {
    if (selectedEntity) {
      destinationTableRef.current.focus();
      return;
    }
    if (destinationTableRef?.current) {
      destinationTableRef.current.blur();
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (editEnabled) {
      let newTableItems = fieldMappings.map((x: any) => {
        return { ...x };
      });
      setEditableFieldMappings([...newTableItems]);
    }
    setEditEnabled(true);
  }, [fieldMappings, editEnabled]);

  useEffect(() => {
    setEditableFieldMappings(
        editableFieldMappings.sort(function (a: any, b: any) {
          return a.toPosition - b.toPosition;
        })
    );
  }, [editableFieldMappings]);

  return (
      <div className="loadfile-tab-container">
        <div className="loadfile-section-container">
          <div>
            <div className="loadfile-section-items">
              {selectedEntity ? (
                  <>
                    {selectedDestinationTable ? (
                        <div className="loadfile-table-container">
                          <Table
                              scroll={{ y: 'calc(100vh - 284px)' }}
                              locale={locale}
                              dataSource={filteredFieldsData.map((x: any) => ({
                                key: x.id,
                                name: x.name,
                              }))}
                              pagination={false}
                              columns={columns}
                              components={components}
                              loading={isTableLoading}
                              size="small"
                              onRow={(_, index) => {
                                const attr = {
                                  index,
                                  mapRow,
                                };
                                return attr as React.HTMLAttributes<any>;
                              }}
                              onChange={handleFieldsDataTableChange}
                          />
                        </div>
                    ) : (
                        <div className="empty-container">
                          <Empty
                              description={t(
                                  "Page.Operation.LoadfileSetup.Tutorial.SelectDestinationTable",
                                  {
                                    lngs: i18n.languages,
                                    defaultValue:
                                        "Please select a destination table to continue",
                                  }
                              )}
                          ></Empty>
                        </div>
                    )}
                  </>
              ) : (
                  <div className="empty-container">
                    <Empty
                        description={t(
                            "Page.Operation.LoadfileSetup.Tutorial.GetStarted",
                            {
                              lngs: i18n.languages,
                              defaultValue: "Please select an Entity to get started",
                            }
                        )}
                    ></Empty>
                  </div>
              )}
            </div>
          </div>
        </div>
        <div className="loadfile-data-container">
          {selectedEntity ? (
              <>
                <div className="title-actions">
                  {/* <span>Mapping</span> */}
                  <Select
                      ref={destinationTableRef}
                      disabled={!selectedEntity}
                      placeholder={t(
                          "Page.Operation.LoadfileSetup.Placeholders.DestinationTable",
                          {
                            lngs: i18n.languages,
                            defaultValue: "Destination table",
                          }
                      )}
                      allowClear
                      loading={isTableLoading}
                      style={{ width: "33%", marginBottom: "5px" }}
                      onChange={handleDestinationChange}
                      value={selectedDestinationTable}
                      options={destinationTables.map((x: any) => ({
                        label: x.name,
                        value: x.id,
                      }))}
                  ></Select>
                  <div className="action-buttons">
                    <Checkbox
                        checked={showMandatoryOnly}
                        onClick={() => setShowMandatoryOnly(!showMandatoryOnly)}
                    >
                      {t(
                          "Page.Operation.LoadfileSetup.Labels.ShowMandatoryOnly",
                          {
                            lngs: i18n.languages,
                            defaultValue: "Show mandatory only",
                          }
                      )}
                    </Checkbox>
                    <Checkbox
                        value={showUnmappedOnly}
                        onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
                    >
                      {t("Page.Operation.LoadfileSetup.Labels.ShowUnmappedOnly", {
                        lngs: i18n.languages,
                        defaultValue: "Show unmapped only",
                      })}
                    </Checkbox>
                    <Checkbox
                        checked={showSystemFields}
                        onClick={() => {
                          setShowSystemFields(!showSystemFields);
                        }}
                    >
                      {t(
                          "Page.Operation.Loadfile.Tabs.Mapping.Labels.ShowSystemFields",
                          {
                            lngs: i18n.languages,
                            defaultValue: "Show system fields",
                          }
                      )}
                    </Checkbox>
                    {editEnabled ? (
                        <>
                          <Button
                              icon={<CloseOutlined />}
                              type="ghost"
                              size="small"
                              onClick={() => {
                                onCancelEdit();
                              }}
                          >
                            {t("General.Labels.Reset", {
                              lngs: i18n.languages,
                              defaultValue: "Reset",
                            })}
                          </Button>
                          <Button
                              icon={<CheckOutlined />}
                              size="small"
                              onClick={() => {
                                onSaveClick();
                              }}
                          >
                            {t("General.Labels.Save", {
                              lngs: i18n.languages,
                              defaultValue: "Save",
                            })}
                          </Button>
                        </>
                    ) : (
                        ""
                    )}
                  </div>
                </div>
                <div className="loadfile-table-container" style={{width: '100%'}}>
                  <Table
                      scroll={scroll}
                      locale={locale}
                      components={tableItemsComponents}
                      dataSource={getMappingsDataSource()}
                      pagination={false}
                      columns={editableColumns as ColumnTypes}
                      size="small"
                      loading={isTableLoading}
                      onRow={(_, index) => {
                        const attr = {
                          index,
                          mapRow,
                        };
                        return attr as React.HTMLAttributes<any>;
                      }}
                      rowClassName={(record: any) => {
                        const classes: string[] = [];
                        if (!record.fromValue && !record.fromField) {
                          classes.push("row-color-grey");
                        }
                        if (record.isSystemField) {
                          classes.push("row-system-field");
                        }
                        return classes.join(" ");
                      }}
                  />
                </div>
              </>
          ) : (
              <div className="empty-container">
                <Empty description={""} />
              </div>
          )}
        </div>
      </div>
  );
};

const DraggableBodyRow = ({
                            index,
                            moveRow,
                            className,
                            style,
                            ...restProps
                          }: DraggableBodyRowProps) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
            dragIndex < index ? " drop-over-downward" : " drop-over-upward",
      };
    },
    drop: (item: { index: number }) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  return (
      <tr
          ref={ref}
          className={`${className}${isOver ? dropClassName : ""}`}
          style={{ cursor: "move", ...style }}
          {...restProps}
      />
  );
};

const DroppableBodyRow: React.FC<DroppableBodyRowProps> = ({
                                                             index,
                                                             mapRow,
                                                             className,
                                                             style,
                                                             ...restProps
                                                           }) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
            dragIndex < index ? " drop-over-downward" : " drop-over-upward",
      };
    },
    drop: (item: { index: number }) => {
      mapRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  const [form] = Form.useForm();

  return (
      <Form form={form} component={false}>
        <EditableContext.Provider value={form}>
          <tr
              ref={ref}
              className={`${className}${isOver ? dropClassName : ""}`}
              style={{ ...style }}
              {...restProps}
          />
        </EditableContext.Provider>
      </Form>
  );
};

export default Mapping;
